import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Search,
  UserPlus,
  Compass,
  MapPin,
  BarChart3,
  Briefcase,
  Coins,
  AlertTriangle,
  Heart,
  ShoppingBag,
  Building2,
  Users,
  PlayCircle,
  Lock,
  Sparkles,
  Award,
  TrendingDown,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import CourseCard from '../../components/parcours/CourseCard';
import AuthModal from '../../components/auth/auth-modal';
import { fetchParcoursIndex } from '../../lib/parcours';
import { useAuth } from '../../AuthProvider';
import type { ParcoursIndexItem } from '../../types/parcours';
import {
  WebPageSchema,
  BreadcrumbSchema,
  FAQPageSchema,
  ProductSchema,
} from '../../lib/utils/schema-utils';

// — Typography aligned on home / campagnes / recrutement / candidat —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass =
  'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Section 2 — Constat (3 pain points) ————————————————————————————
const painPoints = [
  {
    title: 'Le candidat n’a pas vu la vraie réalité avant de signer',
    description:
      "Fiche de poste, deux entretiens, signature : ce que le candidat connaît de votre entreprise au jour J ne représente pas 5 % de ce qu’il va y vivre.",
  },
  {
    title: 'La promesse marketing du recruteur ≠ le quotidien terrain',
    description:
      "Plus la promesse a été embellie, plus la déception est grande au mois 1. Et plus la rupture précoce devient probable.",
  },
  {
    title: 'Aucun mécanisme pour ré-engager le candidat avant le jour J',
    description:
      "Entre la signature et le premier jour, votre candidat continue à comparer, doute, écoute des contre-offres. Vous n’avez aucun outil pour solidifier son engagement.",
  },
];

// — Section 3 — Les 5 étapes du parcours type ——————————————————————
const parcoursSteps = [
  {
    icon: Compass,
    title: 'Les dates clés',
    description:
      "L’histoire vraie de l’entreprise, ses virages, ses moments fondateurs. Pas du storytelling marketing — la matière brute qui explique l’ADN actuel.",
  },
  {
    icon: MapPin,
    title: 'L’ancrage territorial',
    description:
      "Où elle opère réellement, sur quels marchés, avec quels clients-types. La géographie du métier au quotidien.",
  },
  {
    icon: BarChart3,
    title: 'Les chiffres clés',
    description:
      "CA, effectifs, croissance, marges si pertinent. La réalité économique brute, sans embellissement.",
  },
  {
    icon: Briefcase,
    title: 'Votre mission, si vous l’acceptez',
    description:
      "Le poste transformé en mission narrative. Pas une fiche de poste à remplir, un engagement à porter. Format Mission Impossible.",
  },
  {
    icon: Coins,
    title: 'La grille de rémunération',
    description:
      "Le vrai package, affiché AVANT la signature. Aucune surprise au moment de la table de négo. Aucun cabinet français ne le fait. Nous oui.",
  },
];

// — Section 4 — Stats double dividende ————————————————————————————
const dividendStats = [
  {
    icon: TrendingDown,
    metric: '64 %',
    text:
      "des candidats déçus arrêtent d’acheter chez la marque concernée. Source : Talent Board.",
  },
  {
    icon: Heart,
    metric: '88 %',
    text:
      "des candidats avec une bonne expérience deviennent acheteurs des produits — y compris s'ils n'ont pas été retenus. Source : Talent Board.",
  },
  {
    icon: ShoppingBag,
    metric: '91 % vs 26 %',
    text:
      "des employeurs reconnaissent l’impact de l’expérience candidat sur leurs ventes. Seulement 26 % la mesurent. Le gap est votre opportunité.",
  },
];

// — Section 7 — 2 modèles commerciaux ——————————————————————————————
const businessModels = [
  {
    badge: 'Avec retainer',
    icon: Award,
    title: 'Engagement initial. Parcours offert au candidat.',
    description:
      "Vous engagez en amont, nous offrons le Parcours Découverte au candidat retenu. Idéal pour un poste critique avec une exigence de sécurisation maximale.",
    points: [
      'Engagement initial garanti',
      'Parcours Découverte inclus',
      'Garantie replacement < 6 mois',
    ],
  },
  {
    badge: 'Au succès',
    icon: Sparkles,
    title: 'Aucun engagement initial. Production facturée séparément.',
    description:
      "Vous payez uniquement si placement. La production du Parcours Découverte est facturée séparément. Idéal volume, postes ouverts à plusieurs candidats.",
    points: [
      "Pas d'engagement initial",
      'Production Parcours facturée séparément',
      'Idéal volume / récurrence',
    ],
  },
];

export default function ParcoursIndexPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [parcours, setParcours] = useState<ParcoursIndexItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetchParcoursIndex()
      .then((data) => {
        if (!cancelled) setParcours(data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const showGate = !authLoading && !user;

  const scrollToParcoursList = () => {
    document
      .getElementById('parcours-actifs')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Layout>
      <Helmet>
        <title>
          Parcours Découverte · -73% no-show, +40% rétention 12 mois | recruteurs.io
        </title>
        <meta
          name="description"
          content="Faites traverser à vos candidats une plateforme immersive avant signature : -73 % de no-show, +40 % de rétention 12 mois. La méthode signature recruteurs.io."
        />
        <meta
          name="keywords"
          content="parcours candidat, onboarding candidat, onboarding avant embauche, expérience candidat, marque employeur, plateforme parcours découverte, recrutement durable, recruteurs.io"
        />
        <link rel="canonical" href="https://recruteurs.io/decouvrir/" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Schemas JSON-LD : WebPage + Breadcrumb + Product + FAQPage */}
      <WebPageSchema
        page={{
          title:
            'Parcours Découverte · -73% no-show, +40% rétention 12 mois | recruteurs.io',
          description:
            "Faites traverser à vos candidats une plateforme immersive avant signature : -73 % de no-show, +40 % de rétention 12 mois. La méthode signature recruteurs.io.",
          url: 'https://recruteurs.io/decouvrir',
          lastReviewed: '2026-05-18',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          {
            name: 'Plateforme Parcours Découverte',
            url: 'https://recruteurs.io/decouvrir',
          },
        ]}
      />

      <ProductSchema
        product={{
          name: 'Plateforme Parcours Découverte',
          description:
            "Plateforme propriétaire d'onboarding candidat avant signature. Chaque entreprise cliente reçoit un environnement digital sur-mesure que ses candidats traversent à leur rythme — culture, équipe, mission, rémunération — pour décider en connaissance de cause.",
          url: 'https://recruteurs.io/decouvrir',
          brand: 'recruteurs.io',
          category: 'Plateforme de parcours candidat / HRTech',
          image:
            'https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/assets//Recruteurs.io%20Logo%20Final-01.png',
          offers: {
            priceCurrency: 'EUR',
            lowPrice: '1500',
            highPrice: '15000',
            availability: 'InStock',
          },
          aggregateRating: {
            ratingValue: '4.9',
            reviewCount: '7',
            bestRating: '5',
            worstRating: '1',
          },
        }}
      />

      <FAQPageSchema
        questions={[
          {
            question: "Qu'est-ce que la Plateforme Parcours Découverte ?",
            answer:
              "C'est une plateforme propriétaire d'onboarding candidat AVANT signature, développée par recruteurs.io. Chaque entreprise cliente reçoit un environnement digital sur-mesure (vidéos, témoignages, grille de rémunération, parcours d'intégration) que ses candidats traversent à leur rythme pour comprendre la réalité du poste, de l'équipe et de la culture avant de prendre leur décision finale.",
          },
          {
            question:
              "En quoi le Parcours Découverte est-il différent d'un onboarding classique ?",
            answer:
              "L'onboarding classique commence APRÈS la signature, quand le salarié arrive en poste. Le Parcours Découverte commence AVANT la signature : c'est un onboarding pré-embauche. Le candidat décide en connaissance de cause, donc il s'engage davantage et il tient plus longtemps. Résultat moyen observé chez nos clients : -50 % de no-show, -40 % de turnover 90 jours.",
          },
          {
            question:
              'Combien coûte un Parcours Découverte ?',
            answer:
              "Le tarif dépend du périmètre (1 poste critique ou campagne continue) et de l'industrialisation souhaitée. Deux modèles : (1) Production unitaire à partir de 1 500 € (parcours pour 1 mission), (2) Abonnement campagne (vivier continu, plusieurs parcours liés) à partir de 1 200 €/mois. Tarif transparent communiqué au premier cadrage gratuit.",
          },
          {
            question:
              "Combien de temps faut-il pour mettre en place un Parcours Découverte ?",
            answer:
              "Délai standard : 2 à 3 semaines entre le kick-off et la mise en ligne. Le cabinet pilote la production (interviews équipe, captation vidéo, rédaction, intégration plateforme). Le client valide les contenus. Aucune installation logicielle côté client — tout est hébergé chez recruteurs.io.",
          },
          {
            question:
              "Le Parcours Découverte remplace-t-il les entretiens classiques ?",
            answer:
              "Non. Il les complète. Le parcours candidat scénarisé vient EN PLUS des entretiens (RH, opérationnels, dirigeants), pour donner au candidat une vraie photographie de l'entreprise avant qu'il prenne sa décision. Les entretiens restent l'occasion d'évaluer le candidat ; le parcours, l'occasion pour le candidat d'évaluer l'entreprise.",
          },
          {
            question:
              "Pour quel type d'entreprise et de poste le Parcours Découverte fonctionne-t-il ?",
            answer:
              "Particulièrement adapté aux PME et ETI (50-500 collaborateurs) qui recrutent des cadres, dirigeants, commerciaux, fonctions techniques ou postes pivots. Très efficace quand l'enjeu de rétention est fort (postes à fort turnover historique, marchés tendus, secteurs concurrentiels). recruteurs.io intervient principalement à Marseille, Aix-en-Provence, Toulon, Nice et Poitiers.",
          },
        ]}
      />

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />

      {/* ─── HERO ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff]"
      >
        <section className="relative z-10 min-h-[88vh] flex items-center py-10 md:py-12 lg:py-14">
          <div className="w-full px-[5vw] xl:px-[100px] max-w-[1440px] mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs md:text-sm font-semibold text-neutral-500 mb-3 leading-snug uppercase tracking-widest">
                Onboarding candidat avant signature · Plateforme propriétaire · Marseille · Aix · PACA
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-title font-extrabold leading-[1.1] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                Vos candidats voient votre réalité. Avant de signer.
              </h1>
              <p className="mt-5 mb-8 text-neutral-800 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                Un environnement digital sur-mesure construit pour chaque entreprise cliente. Vos candidats le traversent à leur rythme avant la décision finale&nbsp;: ils découvrent votre culture, votre équipe, votre quotidien réel, votre rémunération. <strong>Ils décident en connaissance de cause. Et ils tiennent.</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => navigate('/reservation')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  <Building2 className="w-5 h-5" />
                  Commander un Parcours
                </button>
                <button
                  onClick={scrollToParcoursList}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
                >
                  <Search className="w-5 h-5" />
                  Voir les parcours actifs
                </button>
              </div>
            </div>
          </div>
        </section>
      </SectionBackground>

      {/* ─── SECTION 2 — Constat ─── */}
      <SectionBackground page="decouvrir" section="d-pain" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pourquoi le parcours candidat change tout</span>

            <h2 className={h2Class}>
              La pire période d’un recrutement n’est pas l’entretien.{' '}
              <span className={gradientText}>
                C’est entre la signature et le 90{'­'}e jour.
              </span>
            </h2>

            <p className={sectionSubtitleClass}>
              Entre l’offre signée et la prise de poste, 1 candidat sur 5 se
              rétracte. Dans les 12 mois suivants, 1 cadre sur 5 quitte. Trois
              causes principales — et un <strong>parcours candidat scénarisé en amont</strong>,
              façon onboarding pré-embauche, les traite toutes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {painPoints.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: index * 0.07 }}
                className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <h3 className={h3Class}>{item.title}</h3>
                <p className={bodyClass}>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </Section>
      </SectionBackground>

      {/* ─── SECTION 3 — Les 5 étapes ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-method"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Comment marche le parcours candidat</span>

            <h2 className={h2Class}>
              Cinq étapes d’onboarding candidat,{' '}
              <span className={gradientText}>
                construites sur-mesure pour chaque entreprise cliente.
              </span>
            </h2>

            <p className={sectionSubtitleClass}>
              Le candidat traverse ce parcours candidat à son rythme, depuis chez
              lui, à chaque heure du jour ou de la nuit où il se demande
              «&nbsp;est-ce que je signe&nbsp;?&nbsp;».
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {parcoursSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.07 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-6 h-6 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>
                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-12 rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-4xl mx-auto">
            <p className={`${bodyClass} text-center`}>
              <strong>
                À la fin du parcours, le candidat reprend la parole.
              </strong>{' '}
              Il dit pourquoi il signe — ou pourquoi il se retire. Et c’est
              cette parole-là qui fonde un recrutement durable.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* ─── SECTION 4 — Double dividende ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-dividend"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le bénéfice qui paie deux fois</span>

            <h2 className={h2Class}>
              1 candidat signé.{' '}
              <span className={gradientText}>79 ambassadeurs.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Sur 80 candidats qui traversent un parcours, 1 signe. Les 79
              autres ne disparaissent pas — ils repartent avec votre histoire
              bien racontée. <strong>Plus de 1 000 personnes ont déjà été
              accueillies via la Plateforme Parcours Découverte depuis 2023.</strong>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {dividendStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.metric}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">
                      {stat.metric}
                    </p>
                  </div>
                  <p className={bodyClass}>{stat.text}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* ─── SECTION 5 — Pour qui (2 grandes cartes) ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-fit"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pour qui</span>

            <h2 className={h2Class}>
              Deux audiences,{' '}
              <span className={gradientText}>une seule plateforme.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Carte ENTREPRISES */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5 }}
              className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                  <Building2 className="w-6 h-6" />
                </div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest">
                  Entreprises
                </p>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-[#30475e] leading-tight mb-4">
                Vous voulez sécuriser un recrutement critique
              </h3>
              <p className={`${bodyClass} mb-6`}>
                Le Parcours Découverte est inclus dans nos missions de
                recrutement, ou activable en standalone (onboarding sans
                recrutement). Deux modèles disponibles&nbsp;: retainer ou au succès.
              </p>
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Commander un Parcours
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>

            {/* Carte CANDIDATS */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                  <Users className="w-6 h-6" />
                </div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest">
                  Candidats
                </p>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-[#30475e] leading-tight mb-4">
                Vous voulez découvrir une entreprise avant de candidater
              </h3>
              <p className={`${bodyClass} mb-6`}>
                Créez votre compte gratuit et accédez aux parcours des
                entreprises clientes recruteurs.io. Vous voyez la culture, le
                quotidien, l’équipe avant même d’envoyer une candidature.
              </p>
              <button
                onClick={() => setAuthModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-5 py-2 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                <UserPlus className="w-4 h-4" />
                Créer mon compte candidat
              </button>
            </motion.div>
          </div>
        </Section>
      </SectionBackground>

      {/* ─── SECTION 6 — Liste des parcours actifs (logique préservée) ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-list"
        className="bg-white"
      >
        <Section>
          <div
            id="parcours-actifs"
            className="max-w-4xl mx-auto text-center mb-12 scroll-mt-24"
          >
            <span className={eyebrowClass}>
              Nos clients vous ouvrent leurs portes
            </span>

            <h2 className={h2Class}>
              Les entreprises qui recrutent avec nous,{' '}
              <span className={gradientText}>en ce moment.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Connectez-vous ou créez un compte pour découvrir leur culture,
              leur équipe, leurs valeurs et leur quotidien réel.
            </p>
          </div>

          {/* Cards area with gate for anonymous */}
          <div className="relative">
            <div
              className={
                showGate ? 'blur-md pointer-events-none select-none' : ''
              }
            >
              {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="rounded-2xl ring-1 ring-neutral-200 bg-white overflow-hidden animate-pulse"
                    >
                      <div className="h-44 bg-neutral-100" />
                      <div className="p-5 space-y-3">
                        <div className="h-4 bg-neutral-100 rounded w-3/4" />
                        <div className="h-3 bg-neutral-100 rounded w-full" />
                        <div className="h-3 bg-neutral-100 rounded w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!loading && error && (
                <p className="text-neutral-500 text-sm text-center">
                  Une erreur est survenue lors du chargement. Veuillez réessayer
                  plus tard.
                </p>
              )}

              {!loading && !error && parcours.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <Search className="w-10 h-10 text-neutral-300 mb-4" />
                  <p className="text-neutral-500 text-base font-medium">
                    Aucun parcours publié pour le moment.
                  </p>
                  <p className="text-neutral-400 text-sm mt-1">
                    Revenez bientôt — de nouveaux parcours arrivent
                    régulièrement.
                  </p>
                </div>
              )}

              {!loading && !error && parcours.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {parcours.map((p) => (
                    <CourseCard key={p.id} parcours={p} />
                  ))}
                </div>
              )}
            </div>

            {/* Gate overlay */}
            {showGate && (
              <div className="absolute inset-0 z-10 flex items-center justify-center px-4">
                <div className="bg-white rounded-[1.75rem] shadow-2xl ring-1 ring-neutral-200 p-8 text-center max-w-md w-full">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center mx-auto mb-5 shadow-lg shadow-[#d03484]/20">
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#30475e] mb-2">
                    Accès candidats et entreprises
                  </h3>
                  <p className="text-sm text-neutral-500 mb-6 leading-relaxed">
                    Connectez-vous ou créez un compte gratuit pour découvrir les
                    coulisses de nos entreprises clientes.
                  </p>
                  <div className="space-y-3">
                    <button
                      onClick={() => setAuthModalOpen(true)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-5 py-2 text-sm font-bold whitespace-nowrap text-white shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                    >
                      Se connecter
                      <ArrowRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setAuthModalOpen(true)}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl ring-1 ring-neutral-200 px-5 py-2.5 text-sm font-semibold whitespace-nowrap text-neutral-700 hover:bg-neutral-50 transition-all"
                    >
                      <UserPlus className="w-4 h-4" />
                      Créer un compte candidat
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Section>
      </SectionBackground>

      {/* ─── SECTION 7 — 2 modèles commerciaux ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-models"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Deux modèles pour commander</span>

            <h2 className={h2Class}>
              Comment vous accédez{' '}
              <span className={gradientText}>à la Plateforme.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Selon votre niveau de risque et la nature du recrutement, vous
              choisissez la formule qui colle. Tarifs présentés en transparence
              dès le premier appel.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {businessModels.map((model, index) => {
              const Icon = model.icon;
              return (
                <motion.div
                  key={model.badge}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className="inline-flex items-center rounded-full bg-[#fbfaf8] px-4 py-1.5 text-xs font-semibold text-neutral-700 uppercase tracking-widest ring-1 ring-neutral-200">
                      {model.badge}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#30475e] leading-tight mb-3">
                    {model.title}
                  </h3>
                  <p className={`${bodyClass} mb-5`}>{model.description}</p>

                  <ul className="space-y-2">
                    {model.points.map((point) => (
                      <li
                        key={point}
                        className="flex items-start gap-2 text-sm text-neutral-700"
                      >
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#d03484] to-[#d47254] mt-2" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <a
              href="/infos/tarifs"
              className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-5 py-2 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
            >
              Voir les tarifs détaillés
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </Section>
      </SectionBackground>

      {/* ─── SECTION 8 — Vu à la télé ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-press"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>
              <PlayCircle className="w-4 h-4 mr-2" />
              Vu à la télé
            </span>

            <h2 className={h2Class}>
              Notre vision du recrutement,{' '}
              <span className={gradientText}>expliquée à l’antenne.</span>
            </h2>
          </div>

          <div className="max-w-4xl mx-auto rounded-[2rem] overflow-hidden ring-1 ring-neutral-200 shadow-xl">
            <div
              className="relative w-full"
              style={{ paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src="https://www.youtube.com/embed/sXY1BChmGSc"
                title="recruteurs.io passe à la télé — vision du recrutement"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* ─── SECTION 9 — CTA final dual ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-cta"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Prêt à voir,{' '}
              <span className={gradientText}>ou à faire voir&nbsp;?</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Vous êtes une entreprise qui veut sécuriser ses recrutements ?
              Vous êtes un candidat qui veut comprendre une boîte avant de
              candidater ? Vous êtes au bon endroit.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <Building2 className="w-5 h-5" />
                Commander un Parcours pour ma boîte
              </button>
              <button
                onClick={() => setAuthModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                <UserPlus className="w-5 h-5" />
                Créer mon compte candidat
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* ─── SECTION 9 bis — FAQ ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-faq"
        className="bg-white"
      >
        <Section>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <span className={eyebrowClass}>Questions fréquentes</span>
              <h2 className={h2Class}>
                Le Parcours Découverte,{' '}
                <span className={gradientText}>en clair.</span>
              </h2>
            </div>

            <div className="space-y-3">
              {[
                {
                  q: "Qu'est-ce que la Plateforme Parcours Découverte ?",
                  a: "C'est une plateforme propriétaire d'onboarding candidat AVANT signature, développée par recruteurs.io. Chaque entreprise cliente reçoit un environnement digital sur-mesure (vidéos, témoignages, grille de rémunération, parcours d'intégration) que ses candidats traversent à leur rythme pour comprendre la réalité du poste, de l'équipe et de la culture avant de prendre leur décision finale.",
                },
                {
                  q: "En quoi le Parcours Découverte est-il différent d'un onboarding classique ?",
                  a: "L'onboarding classique commence APRÈS la signature, quand le salarié arrive en poste. Le Parcours Découverte commence AVANT la signature : c'est un onboarding pré-embauche. Le candidat décide en connaissance de cause, donc il s'engage davantage et il tient plus longtemps. Résultat moyen observé : -50 % de no-show, -40 % de turnover 90 jours.",
                },
                {
                  q: "Combien coûte un Parcours Découverte ?",
                  a: "Le tarif dépend du périmètre. Deux modèles : (1) Production unitaire à partir de 1 500 € (parcours pour 1 mission), (2) Abonnement campagne (vivier continu, plusieurs parcours liés) à partir de 1 200 €/mois. Tarif transparent communiqué au premier cadrage gratuit.",
                },
                {
                  q: "Combien de temps faut-il pour mettre en place un Parcours Découverte ?",
                  a: "Délai standard : 2 à 3 semaines entre le kick-off et la mise en ligne. Le cabinet pilote la production (interviews équipe, captation vidéo, rédaction, intégration plateforme). Le client valide les contenus. Aucune installation logicielle côté client.",
                },
                {
                  q: "Le Parcours Découverte remplace-t-il les entretiens classiques ?",
                  a: "Non. Il les complète. Le parcours candidat scénarisé vient EN PLUS des entretiens, pour donner au candidat une vraie photographie de l'entreprise avant qu'il prenne sa décision. Les entretiens restent l'occasion d'évaluer le candidat ; le parcours, l'occasion pour le candidat d'évaluer l'entreprise.",
                },
                {
                  q: "Pour quel type d'entreprise et de poste ça marche ?",
                  a: "Particulièrement adapté aux PME et ETI (50-500 collaborateurs) qui recrutent des cadres, dirigeants, commerciaux, fonctions techniques ou postes pivots. Très efficace quand l'enjeu de rétention est fort (postes à fort turnover historique, marchés tendus). Marseille, Aix-en-Provence, Toulon, Nice et Poitiers principalement.",
                },
              ].map((item, idx) => (
                <details
                  key={idx}
                  className="group rounded-2xl bg-white ring-1 ring-neutral-200 hover:ring-[#d03484]/30 transition-all"
                >
                  <summary className="flex items-center justify-between gap-3 cursor-pointer px-5 py-4 text-sm md:text-base font-semibold text-[#30475e] hover:text-[#d03484] transition-colors">
                    <span>{item.q}</span>
                    <span className="flex-shrink-0 text-[#d03484] text-xl leading-none group-open:rotate-45 transition-transform">
                      +
                    </span>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-neutral-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* ─── SECTION 10 — Bloc texte SEO ─── */}
      <SectionBackground
        page="decouvrir"
        section="d-seo"
        className="bg-[#fbfaf8]"
      >
        <Section>
          <div className="max-w-4xl mx-auto">
            <h2 className={`${h2Class} text-center`}>
              La Plateforme Parcours Découverte,{' '}
              <span className={gradientText}>en quelques mots.</span>
            </h2>

            <div className="space-y-5 text-base md:text-lg text-neutral-700 leading-relaxed mt-10">
              <p>
                La <strong>Plateforme Parcours Découverte</strong> est une
                solution propriétaire conçue par recruteurs.io et utilisée
                depuis 2023 sur les missions de recrutement à Marseille,
                Aix-en-Provence et toute la PACA. Elle a déjà accueilli plus
                de <strong>1 000 personnes</strong> — candidats en process de
                recrutement et collaborateurs en pré-onboarding.
              </p>
              <p>
                Pour les entreprises, c’est un outil de{' '}
                <strong>sécurisation du recrutement</strong>&nbsp;: vos
                candidats voient la réalité avant de signer, ils se ré-engagent
                en connaissance de cause, vous éliminez les no-show au jour J
                et vous divisez la casse des 90 premiers jours. Pour les
                fonctions d’<a href="/entreprise/recrutement" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">expertise et de management</a>, comme pour les recrutements en
                <a href="/entreprise/campagnes" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">{' '}volume sur vivier</a>.
              </p>
              <p>
                Pour les candidats, c’est l’occasion de{' '}
                <strong>tout savoir avant de dire oui</strong>&nbsp;: l’histoire
                de l’entreprise, son ancrage territorial, ses chiffres, la
                mission proposée et — fait rare en France —{' '}
                <strong>la grille de rémunération réelle</strong>, affichée
                avant la table de négociation.
              </p>
              <p>
                Le tout est facturé selon deux modèles : avec retainer ou au
                succès, selon la nature du poste et votre niveau d’engagement.{' '}
                <a href="/infos/tarifs" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">
                  Voir les tarifs détaillés
                </a>
                , ou{' '}
                <a href="/cabinet-recrutement-marseille" className="font-semibold text-[#d03484] underline-offset-4 hover:underline">
                  parcourir nos missions de recrutement à Marseille
                </a>
                .
              </p>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
